import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled from 'styled-components';

import { isHTMLElement } from '../../core/Env';
import { MODAL_CLASS_NAME } from '../../core/Modal';
import { useIORef, useShowable } from '../../core/hooks';
import { useStacked } from '../Keyboard';

import { ctxClass } from '../utils';

export const ctxModalClass = ctxClass(MODAL_CLASS_NAME);

/**
 * Это наши алгебры
 */

/**
 * Первая алгебра предназначена для первичной инициализации
 */
interface Init<A> {
    init: () => A,
}


/**
 * Вторая алгебра предназначена для переопределения
 */
interface Rendered<A> {
    rendered: () => A,
}

interface Set<A, B> {
    set: (x: A) => B,
}

interface Incementable<A> {
    inc: (x: number) => A,
}





interface Actualable {
    isActual: boolean
}

const actualInstance: Init<Actualable> & Rendered<Actualable> = {
    init: () => ({ isActual: false }),
    rendered: () => ({ isActual: true }),
}


interface Showable {
    showed: boolean,
}

const showInstance: Init<Showable> & Set<boolean, Showable> = {
    init: () => ({ showed: false }),
    set: (x) => ({ showed: x }),
}



interface Deepable {
    depth: number,
}

const deepInstance: Init<Deepable> & Incementable<Deepable> = {
    init: () => ({ depth: 0 }),
    inc: (d) => ({ depth: d + 1 }),
}




// -------------------------------------------------------------------------------------
// Model
// -------------------------------------------------------------------------------------


interface IModalContext extends Actualable, Showable, Deepable {

    getFocus: () => boolean,
    focused: () => void,
    blured: () => void,

    id: () => number, 
    modalId: {
        portalId: string,
    },

    isReady: () => boolean,
}

interface IModalProps {
    showed: boolean
}





const ModalContext = React.createContext<IModalContext>({
    ...actualInstance.init(),
    ...showInstance.init(),
    ...deepInstance.init(),

    getFocus: () => false,
    focused: () => {},
    blured: () => {},
    id: () => 0, 
    modalId: {
        portalId: "",
    },
    isReady: () => false,
});


export const useContext = () => {
    const ctx = React.useContext(ModalContext);

    return ctx;
}

export const useControl = () => {
    const { isShowed, show, hide } = useShowable();

    return {
        isShow: isShowed,
        handleClose: hide,
        handleShow: show,
    }
};



export const withPortalModal = (Component: React.FunctionComponent) =>
    (props: object & { portalId?: string } & React.HTMLAttributes<HTMLElement>) => {

        const { isReady } = useContext();

        const getEl = () => document.getElementById(props.portalId!);

        return props.portalId 
            ? (isReady() && isHTMLElement(getEl()) ? ReactDOM.createPortal(<Component {...props} />, getEl() as HTMLElement ) : null)
            : ReactDOM.createPortal(<Component {...props} />, document.body);
    }



// utils

let globalModalId = 0;

const generateModalId = (id: number) => `portal-modal-id${id}`;

const Wrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    z-index: 1000;
    margin: 0 !important;
    padding: 0 !important;
`;

// -------------------------------------------------------------------------------------
// Instance
// -------------------------------------------------------------------------------------

export const Base = React.forwardRef<HTMLDivElement, { children: React.ReactChild } & IModalProps>(({ showed, children }, ref) => {
    const [signal, runSignal] = React.useState(0);

    React.useEffect(() => {
        runSignal(1);
    }, []);

    const isReady = () => signal === 1;
    

    /**
     * Это нужно для того, чтобы зарегистрировать первый элемент, который может быть зафокусен,
     * специальная возможноость для всяких инпутов и иных контролов быть сразу в фокусе 
     * при открытии модалки
     */
    const {
        read: getFocus,
        write: setFocus,
    } = useIORef(false);

    const focused = setFocus(true);
    const blured = setFocus(false);

    /**
     * Определяем уровень вложенности, на случай если мы модалка в модалке, может быть полезным
     */
    const { depth } = React.useContext(ModalContext);


    /**
     * Работа с айдишниками, спецом для объектов вроде тултипов и прочего
     */
    const { read: id } = useIORef(globalModalId++);
    
    const portalId = generateModalId(id());

    useStacked(showed, id());

    return (
        ReactDOM.createPortal(
            <ModalContext.Provider value={{
                    ...actualInstance.rendered(),
                    ...showInstance.set(showed),
                    ...deepInstance.inc(depth),

                    getFocus, 
                    focused,
                    blured,
                    id, 
                    modalId: {
                        portalId,
                    },

                    isReady,
                }}>
                <Wrapper 
                    id={portalId}
                    className={MODAL_CLASS_NAME}
                    ref={ref}>
                    {children}
                </Wrapper>
            </ModalContext.Provider>,
            document.body
        )
    );
});
